import axios from 'axios';
import { BACKEND_URL } from '../config';

const API = `${BACKEND_URL}/api`;

// Create axios instance
const apiClient = axios.create({
  baseURL: API,
  timeout: 30000,
});

// Add auth token to requests
apiClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Handle auth errors
apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/admin/login';
    }
    return Promise.reject(error);
  }
);

// Auth API
export const authApi = {
  login: (credentials) => apiClient.post('/auth/login', credentials),
  logout: () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    window.location.href = '/admin/login';
  },
  getMe: () => apiClient.get('/auth/me'),
};

// Content API (Public)
export const contentApi = {
  getHeroSlides: () => apiClient.get('/content/hero-slides'),
  getServices: () => apiClient.get('/content/services'),
  getCaseStudies: () => apiClient.get('/content/case-studies'),
  getTestimonials: () => apiClient.get('/content/testimonials'),
  submitTestimonial: (data) => apiClient.post('/testimonials/submit', data),
  getShowcaseApps: () => apiClient.get('/content/showcase-apps'),
  getStats: () => apiClient.get('/content/stats'),
};

// Leads API
export const leadsApi = {
  createLead: (leadData) => apiClient.post('/leads', leadData),
  getLeads: (filters = {}) => {
    const params = new URLSearchParams();
    if (filters.status) params.append('status', filters.status);
    if (filters.source) params.append('source', filters.source);
    return apiClient.get(`/leads?${params}`);
  },
  getLead: (id) => apiClient.get(`/leads/${id}`),
  updateLead: (id, data) => apiClient.put(`/leads/${id}`, data),
  deleteLead: (id) => apiClient.delete(`/leads/${id}`),
};

// Admin Content API
export const adminApi = {
  // Analytics
  getAnalytics: () => apiClient.get('/admin/analytics'),

  // Hero Slides
  getHeroSlides: () => apiClient.get('/admin/hero-slides'),
  createHeroSlide: (data) => apiClient.post('/admin/hero-slides', data),
  updateHeroSlide: (id, data) => apiClient.put(`/admin/hero-slides/${id}`, data),
  deleteHeroSlide: (id) => apiClient.delete(`/admin/hero-slides/${id}`),

  // Services
  getServices: () => apiClient.get('/admin/services'),
  createService: (data) => apiClient.post('/admin/services', data),
  updateService: (id, data) => apiClient.put(`/admin/services/${id}`, data),
  deleteService: (id) => apiClient.delete(`/admin/services/${id}`),

  // Case Studies
  getCaseStudies: () => apiClient.get('/admin/case-studies'),
  createCaseStudy: (data) => apiClient.post('/admin/case-studies', data),
  updateCaseStudy: (id, data) => apiClient.put(`/admin/case-studies/${id}`, data),
  deleteCaseStudy: (id) => apiClient.delete(`/admin/case-studies/${id}`),

  // Testimonials
  getTestimonials: () => apiClient.get('/admin/testimonials'),
  createTestimonial: (data) => apiClient.post('/admin/testimonials', data),
  updateTestimonial: (id, data) => apiClient.put(`/admin/testimonials/${id}`, data),
  deleteTestimonial: (id) => apiClient.delete(`/admin/testimonials/${id}`),

  // Projects
  getProjects: () => apiClient.get('/admin/projects'),
  createProject: (data) => apiClient.post('/admin/projects', data),
  updateProject: (id, data) => apiClient.put(`/admin/projects/${id}`, data),
  deleteProject: (id) => apiClient.delete(`/admin/projects/${id}`),

  // Case Studies
  getAdminCaseStudies: () => apiClient.get('/admin/case-studies'),
  createCaseStudy: (data) => apiClient.post('/admin/case-studies', data),
  updateCaseStudy: (id, data) => apiClient.put(`/admin/case-studies/${id}`, data),
  deleteCaseStudy: (id) => apiClient.delete(`/admin/case-studies/${id}`),

  // Showcase Apps
  getShowcaseApps: () => apiClient.get('/admin/showcase-apps'),
  createShowcaseApp: (data) => apiClient.post('/admin/showcase-apps', data),
  updateShowcaseApp: (id, data) => apiClient.put(`/admin/showcase-apps/${id}`, data),
  deleteShowcaseApp: (id) => apiClient.delete(`/admin/showcase-apps/${id}`),

  // Stats
  getStats: () => apiClient.get('/admin/stats'),
  createStat: (data) => apiClient.post('/admin/stats', data),
  updateStat: (id, data) => apiClient.put(`/admin/stats/${id}`, data),
  deleteStat: (id) => apiClient.delete(`/admin/stats/${id}`),

  // Schedules
  getSchedules: () => apiClient.get('/admin/schedules'),
  updateSchedule: (id, data) => apiClient.put(`/admin/schedules/${id}`, data),
  deleteSchedule: (id) => apiClient.delete(`/admin/schedules/${id}`),

  // Employeeships
  getEmployeeships: () => apiClient.get('/admin/employeeships'),
  createEmployeeship: (data) => apiClient.post('/admin/employeeships', data),
  updateEmployeeship: (id, data) => apiClient.put(`/admin/employeeships/${id}`, data),
  deleteEmployeeship: (id) => apiClient.delete(`/admin/employeeships/${id}`),
};

export const employeeshipsApi = {
  getCertificate: (certificateId) => apiClient.get(`/employeeships/${certificateId}`),
  getPublicEmployeeships: () => apiClient.get('/public/employeeships'),
};

export const schedulesApi = {
  createSchedule: (data) => apiClient.post('/schedules', data),
};

export default apiClient;